import { useState, useEffect, useMemo } from 'react';
import { Button, Select, Option } from '@material-tailwind/react';
import { Breadcrumbs } from 'components/breadcrumbs';
import useWindowWidth from 'utills/InitWidthDevice';
import { useTranslation } from '@refinedev/core';
import ApiService from 'utills/ApiService';
import { PHONE_CODE, REGEX_VALID_EMAIL } from 'utills/constants';
import toast from 'react-hot-toast';
import { Formik, Field, Form } from 'formik';
import { SwitchButton } from 'components/switchButton';
import { FullScreenLoading } from 'components/fullScreenLoading';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { get } from 'lodash';
import MultiSelect from 'react-select';
import './create.scss';
import { useNavigate } from 'react-router-dom';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const FormCreateAccount = (props: any) => {
  const { translate, isMobile, token, language } = props;
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(true);
  const [role, setRole] = useState('');
  const [event, setEvent] = useState([]);
  const [roleList, setRoleList] = useState([] as any);

  const [events, setEvents] = useState([] as any);
  const [error, setError] = useState({
    role: '',
    event: ''
  } as any);
  const [fetchData, setFetchData] = useState(false);

  useEffect(() => {
    if (token !== '') {
      const intervalFetchData = setInterval(() => {
        setFetchData(true);
        clearInterval(intervalFetchData);
      }, 100);
    }
  }, [token]);

  useEffect(() => {
    if (fetchData) {
      fetchEventList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [fetchData]);

  const fetchEventList = async () => {
    setLoading(true);
    try {
      const result = await ApiService.get(`events`, token);

      if (result) {
        const arr = [] as any;

        result.forEach((item: any) => {
          const obj = {
            value: item.id,
            label: get(item, `name_${language}`, '')
          };

          arr.push(obj);
        });

        setEvents(arr);
        setLoading(false);
      }
    } catch (error: any) {
      toast.error(error.message);
      setLoading(false);
    }
  }

  const [phoneCode, setPhoneCode] = useState('84');
  const navigate = useNavigate();

  useEffect(() => {
    if (roleList.length === 0 && fetchData) {
      fetchListRole();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, roleList]);

  const fetchListRole = async () => {
    setLoading(true);
    try {
      const result = await ApiService.get(`role/all/ids`, token);

      if (result) {
        const roles = [] as any;

        result.forEach((item: any) => {
          if (item.name.toLowerCase() !== "owner") {
            const obj = {
              value: item.id,
              label: item.name
            };

            roles.push(obj);
          }
        });

        if (roles.length > 0) {
          setRoleList([...roles]);
        }
      }
    } catch (error: any) {
      toast.error(error.message);
    }
    setLoading(false);
  }

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    setLoading(true);

    const eventSelected: Array<string> = [];

    event.forEach((item: any) => {
      eventSelected.push(item.value);
    })

    const submitData = {
      "email": values.email,
      "first_name": values.first_name,
      "last_name": values.last_name,
      "role_id": role,
      "phone_area_code": phoneCode,
      "phone": values.phone,
      "event_ids": eventSelected,
      "is_active": status
    }

    const err = { ...error } as any;

    if (role === '') {
      err['role'] = translate('error-field-require');
    } else {
      err['role'] = '';
    }

    if (eventSelected.length === 0) {
      err['event'] = translate('error-field-require');
    } else {
      err['event'] = '';
    }

    setError({ ...err });

    if (err.role === '' && err.event === '') {
      try {
        const result = await ApiService.post(`account`, submitData, token);

        if (result) {
          toast.success(translate("create-successfully"))

          const redirect = setInterval(() => {
            navigate("/organization/account");
            clearInterval(redirect);
          }, 2000);
        }
      } catch (error: any) {
        toast.error(error.message);
      }
    }

    setSubmitting(false);
    setLoading(false);
  }

  const validateEmail = (value: string) => {
    let error;

    if (!value) {
      error = translate('required-email');
    } else {
      if (!REGEX_VALID_EMAIL.test(value)) {
        error = translate('wrong-format-email');
      }
    }

    return error;
  }

  const validateText = (value: string) => {
    let error;

    if (!value) {
      error = translate("error-field-require");
    } else {
      let str = value;
      str = str.toLowerCase();
      str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a');
      str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e');
      str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i');
      str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o');
      str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u');
      str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y');
      str = str.replace(/đ/g, 'd');
      const regex = /[^a-zA-Z0-9 ]+/;

      if (regex.test(str)) {
        error = translate("error-special-character");
      }
    }

    return error;
  }

  const eventOptions = useMemo(() => {
    // Check all-event option is selected
    const allEventSelected = event?.find((e: { value: string, label: string }) => {
      return e?.value === "all";
    });

    // When user select all-event option
    if (event?.length !== 0 && allEventSelected) return [];

    return events;
  }, [events, event]);

  return (
    <div className={`${isMobile ? 'w-full' : 'w-[80%]'} bg-white rounded-[8px]`} id='form-edit-account'>
      <Formik
        initialValues={{ email: '', first_name: '', last_name: '', phone: '' } as any}
        onSubmit={(values, { setSubmitting }) => handleSubmit(values, { setSubmitting })}
      >
        {({
          handleSubmit,
          isSubmitting
        }) => (
          <Form onSubmit={handleSubmit}>
            <div className="w-full">
              <div className="w-full flex items-center h-[58px] px-[24px] border-b border-[#c5ccd2] text-16 font-600">{translate('create-new-account')}</div>

              <div className={`${isMobile ? 'p-[16px]' : 'p-[24px]'} w-full grid grid-cols-1 gap-[16px]`}>
                <div className={`${isMobile ? 'grid-cols-1' : 'grid-cols-2'} w-full grid gap-[16px]`}>
                  <div className="w-full">
                    <div className="w-full flex items-center gap-x-[4px] text-14 font-500 mb-[8px]">
                      <span className="form-label">{translate('status')}</span>

                      <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />
                    </div>

                    <SwitchButton
                      checked={status}
                      onChange={() => setStatus(!status)} />
                  </div>

                  <div className="w-full">
                    <div className="w-full flex items-center gap-x-[4px] text-14 font-500 mb-[8px]">
                      <span className="form-label">Email</span>

                      <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />

                      <LightTooltip title={translate('tooltip-email-create-account')} className="w-[200px]" placement='top-start'>
                        <img src="/images/icons/tooltip.svg" alt="icon-tooltip" />
                      </LightTooltip>
                    </div>

                    <div className="field-group">
                      <Field name="email" validate={validateEmail}>
                        {({
                          field,
                          meta,
                        }: any) => (
                          <div className="field-group">
                            <input {...field} type="text" className="w-full h-[40px] text-14 rounded-[4px] border border-[#c5ccd2] text-[#868e95]" autoComplete="off" placeholder='Email' />

                            {meta.touched && meta.error && (
                              <div className="text-12 text-red-500 mt-[4px]">{meta.error}</div>
                            )}
                          </div>
                        )}
                      </Field>
                    </div>

                  </div>
                </div>

                <div className={`${isMobile ? 'grid-cols-1' : 'grid-cols-2'} w-full grid gap-[16px]`}>
                  <div className="w-full">
                    <div className="w-full flex items-center gap-x-[4px] text-14 font-500 mb-[8px]">
                      <span className="form-label">{translate('first-name')}</span>

                      <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />
                    </div>

                    <Field name="first_name" validate={validateText}>
                      {({
                        field,
                        meta,
                      }: any) => (
                        <div className="field-group">
                          <input {...field} type="text" className="w-full h-[40px] text-14 rounded-[4px] border border-[#c5ccd2]" autoComplete="off" placeholder={translate('first-name')} maxLength={50} />

                          <div className="text-length text-right">{field.value.length}/50</div>

                          {meta.touched && meta.error && (
                            <div className="text-14 text-red-500 mt-[4px]">{meta.error}</div>
                          )}
                        </div>
                      )}
                    </Field>

                  </div>

                  <div className="w-full">
                    <div className="w-full flex items-center gap-x-[4px] text-14 font-500 mb-[8px]">
                      <span className="form-label">{translate('last-name')}</span>

                      <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />
                    </div>

                    <Field name="last_name" validate={validateText}>
                      {({
                        field,
                        meta,
                      }: any) => (
                        <div className="field-group">
                          <input {...field} type="text" className="w-full h-[40px] text-14 rounded-[4px] border border-[#c5ccd2]" autoComplete="off" placeholder={translate('last-name')} maxLength={50} />

                          <div className="text-length text-right">{field.value.length}/50</div>

                          {meta.touched && meta.error && (
                            <div className="text-14 text-red-500 mt-[4px]">{meta.error}</div>
                          )}
                        </div>
                      )}
                    </Field>

                  </div>
                </div>

                <div className={`${isMobile ? 'grid-cols-1' : 'grid-cols-2'} w-full grid gap-[16px]`}>
                  <div className="w-full">
                    <div className="w-full flex items-center gap-x-[4px] text-14 font-500 mb-[8px]">
                      <span className="form-label">{translate('role-name')}</span>

                      <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />
                    </div>

                    <div className="select-role">
                      {
                        roleList.length > 0 &&
                        <Select
                          value={role}
                          onChange={(val: any) => setRole(val)}
                          {...{} as any}
                        >
                          {
                            roleList.map((item: any, index: number) => {
                              return (
                                <Option key={`${item.value}_${index}`}
                                  value={item.value}>{item.label}</Option>
                              )
                            })
                          }
                        </Select>
                      }

                    </div>
                  </div>

                  <div className="form-item">
                    <div className="w-full flex items-center gap-x-[4px] text-14 font-500 mb-[8px]">
                      <span className="form-label">{translate('contact-phone')}</span>
                    </div>

                    <div className="phone-group">
                      <div className="select-phone-code">
                        <Select
                          value={phoneCode}
                          onChange={(val: any) => setPhoneCode(val)}
                          {...{} as any}
                          className='border-none'
                        >
                          {
                            PHONE_CODE.map((item: any, index: number) => {
                              return (
                                <Option key={`${item.value}_${index}`}
                                  value={item.code}>{item.value} | +{item.code}</Option>
                              )
                            })
                          }
                        </Select>
                      </div>

                      <Field name="phone" >
                        {({
                          field,
                          meta,
                        }: any) => (
                          <div className="input-phone-group">
                            <input {...field} type="text" className="input-phone" autoComplete="off" placeholder={translate('phone-number')} />
                          </div>
                        )}
                      </Field>
                    </div>

                  </div>
                </div>

                <div className="w-full">
                  <div className="form-item-full">
                    <div className="w-full flex items-center gap-x-[4px] text-14 font-500 mb-[8px]">
                      <span className="form-label">{translate('event')}</span>

                      <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />
                    </div>

                    <MultiSelect
                      styles={{
                        input: (base) => ({
                          ...base,
                          "input:focus": {
                            boxShadow: "none",
                          },
                        })
                      }}
                      // defaultValue=""
                      value={event}
                      isMulti
                      name="categories"
                      options={eventOptions}
                      className="multi-select"
                      classNamePrefix="select"
                      placeholder={translate('select-event')}
                      onChange={(e: any) => {
                        const err = { ...error } as any;

                        const allEventSelected = e?.find((event: { value: string, label: string }) => {
                          return event?.value === "all";
                        });

                        // When user choose all-event option
                        if (allEventSelected) {
                          setEvent([allEventSelected] as any);
                          err['event'] = '';

                          // Reset event error
                          setError({ ...err });

                          return;
                        }
                        
                        if (e.length === 0) {
                          setEvent(e);
                          err['event'] = translate('error-field-require')
                        } else {
                          setEvent(e);
                          err['event'] = '';
                        }

                        setError({ ...err });
                      }} />

                    {
                      (error && error.event !== '') &&
                      <div className="text-14 text-red-500 mt-[4px]">{error.event}</div>
                    }

                  </div>
                </div>
              </div>
            </div>

            <div className="w-full h-[80px] flex items-center justify-end gap-x-[12px] px-[24px] absolute bottom-0 left-0 bg-white">
              <Button
                {...{} as any} className="px-[24px] h-[48px] flex items-center justify-center text-16 font-500 bg-white border border-brand-500 text-brand-500" onClick={() => window.history.back()}>{translate('btn-cancel')}</Button>
              <Button
                {...{} as any}
                type="submit" disabled={isSubmitting} className="px-[24px] h-[48px] flex items-center justify-center text-16 font-500 bg-brand-500 border border-brand-500 text-white">{translate('btn-save')}</Button>
            </div>

            {
              loading &&
              <FullScreenLoading />
            }
          </Form>
        )}
      </Formik>

    </div>
  )
}

const WPSAccountCreate = () => {
  const windowWidth = useWindowWidth();
  const [isMobile, setIsMobile] = useState(false);
  const { translate, getLocale } = useTranslation();
  const language = getLocale();
  const [token, setToken] = useState('');

  useEffect(() => {
    const user = localStorage.getItem("USER");

    if (user) {
      const userData = JSON.parse(user);
      setToken(userData?.token);
    }
  }, []);

  const breadcrumbItems = [
    {
      label: translate('organization'),
      href: ''
    },
    {
      label: translate('account-management'),
      href: '/organization/account'
    },
    {
      label: translate('create-new-account'),
      href: ''
    },
  ];

  useEffect(() => {
    if (windowWidth && windowWidth < 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowWidth]);

  return (
    <div className="w-full">
      <Breadcrumbs type="wps" breadcrumbItems={breadcrumbItems} />

      {
        (token !== '') &&
        <div className={`w-full ${isMobile ? 'p-[16px] pb-[70px]' : 'pt-[16px] px-[10%] pb-[100px] flex justify-center'}`}>
          <FormCreateAccount
            isMobile={isMobile}
            translate={translate}
            token={token}
            language={language} />
        </div>
      }

    </div>
  )
};

export default WPSAccountCreate;